import { Component, ViewChild, Injector } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { Store } from '@ngrx/store';
import { AppLoginSubjectService, Pagination, TenantUserRoleService } from 'blg-akaun-ts-lib';
import { PermissionFacade } from '../../../../../../modules/permission/facades/permission.facade';
import { gridOptionsServerSide, pageFiltering, pageSorting, serverSideClear } from '../../../../../../listing.utils';
import { PaginationComponent } from '../../../../../../utilities/pagination/pagination.component';
import { ViewColumnComponent } from '../../../../../../view-column.component';
import { AppConfig } from '../../../../../../visa';
import { forkJoin, iif, Observable, of } from 'rxjs';
import { filter, map, mergeMap, switchMap } from 'rxjs/operators';
import { SubSink } from 'subsink2';
import { PermissionStates } from '../../../../permission-controller/root';
import { RolePermActions } from '../../../../role-permissions-controller/actions';
import { RolePermSelectors } from '../../../../role-permissions-controller/selectors';
import { TranslateService } from "@ngx-translate/core";

interface LocalState {
  deactivateReturn: boolean;
  selectedIndex: number;
  deactivateAdd: boolean;
  deactivateList: boolean;
}

@Component({
  selector: 'app-role-permission-user',
  templateUrl: './role-permission-user.component.html',
  styleUrls: ['./role-permission-user.component.scss']
})
export class RolePermissionUserComponent extends ViewColumnComponent {

  @ViewChild(PaginationComponent, {static: false})
  private paginationComponent: PaginationComponent;

  protected readonly index = 2;
  private localState: LocalState;

  // initialise local states
  readonly localState$ = this.viewColFacade.selectLocalState(this.index);
  readonly deactivateAdd$ = this.componentStore.select(state => state.localState.deactivateAdd);
  readonly deactivateList$ = this.componentStore.select(state => state.localState.deactivateList);

  // api visa
  apiVisa = AppConfig.apiVisa;

  searchValue: any = '';

  // initial ag grid state
  gridApi;
  gridOptions = gridOptionsServerSide;
  columnsDefs;
  rowSelection = 'multiple';
  rowData = [
    // {
    //   name: 'Vicky',
    //   email: 'vicky@wavelet.net',
    //   mobile: '+60176356293',
    //   rank: 'OWNER'
    // },
    // {
    //   name: 'Car',
    //   email: 'car@wavelet.net',
    //   mobile: '+60176356293',
    //   rank: 'ADMIN'
    // }
  ];

  // subSink is to ensure all subscribe calls are unsubscribed to once the component is destroyed
  private subSink = new SubSink;

  roleGuid: string;

  //translation variable
  create_button: string;
  search_text: string;
  search_name_or_mobile_text: string;
  email_text: string;
  mobile_text: string;
  name_text: string;
  remove_button: string;

  constructor(
    private tenantUserRoleService: TenantUserRoleService,
    // private appLoginPrincipalService: AppLoginPrincipalService,
    private appLoginSubjectService: AppLoginSubjectService,
    private readonly store: Store<PermissionStates>,
    private viewColFacade: PermissionFacade,
    private componentStore: ComponentStore<{localState: LocalState}>,
    private injector: Injector
  ) {
    super();
    const translateService = this.injector.get(TranslateService, null);
    if (translateService) {
      translateService.get('labels').subscribe(res => {
        this.create_button = res.create_button_nocaps;
        this.search_text = res.search;
        this.search_name_or_mobile_text = res.search_name_or_mobile;
        this.email_text = res.email;
        this.mobile_text = res.mobile;
        this.name_text = res.name;
        this.remove_button = res.remove_button_caps;
      });
    } else {
      this.create_button = 'Create';
      this.search_text = 'Search';
      this.search_name_or_mobile_text = 'Search user name or mobile'
      this.email_text = 'Email';
      this.mobile_text = 'Mobile';
      this.name_text = 'Name';
      this.remove_button = 'REMOVE';
    }
  }

  /**
   * Lifecycle method: Called when component is first initialised.
   */
   ngOnInit() {
    // initial configurations for local state
    this.subSink.sink = this.localState$.subscribe( a => {
      this.localState = a;
      this.componentStore.setState( {localState: a} );
    });

    this.columnsDefs = [
      {
        headerName: '',
        field: 'app_mst_link_subject_to_role.guid',
        valueFormatter: params => '',
        checkboxSelection: true,
        floatingFilter: false,
        sortable: false,
        minWidth: 0,
      },
      {
        headerName: this.email_text,
        field: 'main_email',
        cellStyle: {textAlign: 'left'}
      },
      {
        headerName: this.mobile_text,
        field: 'main_phone',
        cellStyle: {textAlign: 'left'}
      },
      {
        headerName: this.name_text,
        field: 'name',
        cellStyle: {textAlign: 'left'}
      }
    ];
  }

  /**
   * This function is called when the user clicks on the Add button in the UI.
   * Will trigger the add component to be shown on the screen.
   *
   */
   onNext() {
    // deactivate return, add and list in this component
    this.viewColFacade.updateInstance(this.index,
      {
        ...this.localState,
        deactivateReturn: true,
        deactivateAdd: true,
        deactivateList: true
      });
    this.viewColFacade.onNextAndReset(this.index, 3);
  }

  /**
   * This function is called when AG grid is first initialised and initally
   * sets the datasource of the grid.
   *
   * @param params : grid parameters
   */
   onGridReady(params) {
    this.gridApi = params.api;
    this.gridApi.closeToolPanel();

    this.subSink.sink = this.store.select(RolePermSelectors.selectGuid).subscribe(guid => {
      if (guid) {
        this.roleGuid = guid;
        const datasource = this.getDatasource();
        this.gridApi.setServerSideDatasource(datasource);

        // resize columns
        const allColIds = params.columnApi.getAllColumns().map(x => x.colId);
        params.columnApi.autoSizeColumns(allColIds);
      }
    });

    this.subSink.sink = this.store.select(RolePermSelectors.selectUpdateUserRoleListing)
    .subscribe(x => {
      if (x) {
        if (this.gridApi.getDisplayedRowCount() > 0) {
          this.gridApi.refreshServerSideStore();
        } else {
          this.gridApi.refreshServerSideStore(
            {purge: true}
          )
        }
        this.store.dispatch(RolePermActions.setUpdateUserRoleListing({update: false}))
      }
    })
  }

  /**
   * Returns the datasource for the AG grid.
   *
   * @param conditionalCriteria : any custom criteria to search for in the API
   * @returns the datasource
   */
  getDatasource(conditionalCriteria?) {

    let offset = 0;
    let limit = this.paginationComponent.rowPerPage;

    let criteria = [
      {columnName: 'calcTotalRecords', operator: '=', value: 'true'},
      {columnName: 'app_mst_role_guid', operator: '=', value: this.roleGuid}
    ];

    if (conditionalCriteria) {
      criteria = criteria.concat(conditionalCriteria);
    }

    return {
      getRows: grid => {

        const sortModel = grid.request.sortModel;
        const filterModel = grid.request.filterModel;
        const sortOn = pageSorting(sortModel);
        const filter = pageFiltering(filterModel);

        // only change offset and limit if no filterModel or sortModel is present
        if (Object.keys(filterModel).length == 0 && sortModel.length == 0) {
          offset = grid.request.startRow;
          limit = grid.request.endRow - offset;
        }

        const pagination = new Pagination(offset, limit, criteria);
        this.subSink.sink = this.tenantUserRoleService.getByCriteria(pagination, this.apiVisa)
          .pipe(
            mergeMap(a => {
              const source: Observable<any>[] = [];
              a.data.forEach((b) => {
                // const newModel: UserRoleModel = new UserRoleModel();
                // newModel.app_mst_link_subject_to_role = b.link_subject_to_role;
                const user$ = this.getUser(b.link_subject_to_role.app_subject_guid.toString())
                .pipe(map(user => ({
                  ...user,
                  guid: b.link_subject_to_role.guid // Add b.guid to the user object
                })));
                // const result$ = user$
                //   .pipe(
                //     map(users => {
                //       users.forEach(user => {
                //         // assign details depending on whether it is a mobile number or an email
                //         const isMobile = user.app_login_principal.principal_type == 'MOBILE_USERNAME' ? true: false;
                //         if (isMobile) {
                //           newModel.app_login_principal_mobile = user.app_login_principal;
                //         } else {
                //           newModel.app_login_principal_email = user.app_login_principal;
                //         }
                //       })
                //       return newModel;
                //     })
                //   )
                // push new model observable into array
                source.push(user$);
              });
              return iif(() => source.length > 0,
              forkJoin(source).pipe(map(x => {
                return {...a,
                data: x}
              })),
              of({...a, data: [], totalRecords: 0}))
            })
          ).subscribe(a => {
            console.log(a)
            // put data into the grid
            const data = sortOn(a.data).filter(entity => filter.by(entity));
            const totalRecords = filter.isFiltering ? a.totalRecords : data.length;
            grid.successCallback(data, totalRecords);
          }, err => {
            grid.failCallback();
          })
      }
    }

  }

  /**
   * Return the AppLoginPrincipalContainerModel of the subject with subjectGuid
   * as sepcified in the parameter.
   *
   * @param subjectGuid : the subject guid to the name of
   * @returns an observable of type AppLoginPrincipalContainerModel which
   * contains information of the subject
   */
   getUser(subjectGuid: string) {
    // initialise new pagination
    const pagination = new Pagination();
    // add necessary criteria
    pagination.conditionalCriteria.push({
      columnName: 'guids',
      operator: '=',
      value: subjectGuid
    });

    return this.appLoginSubjectService.getByCriteria(pagination, this.apiVisa)
      .pipe(
        map((subject) => {
          return subject.data[0]})
      )

  }

  /**
   * Remove the selected users from the role.
   */
  onRemove() {
    // find the links to delete
    const selectedNodes = this.gridApi.getSelectedNodes();
    const guids = selectedNodes.map(node => node.data.guid);
    console.log("guids",guids);
    // .map(node => node.data.link_subject_to_role.guid.toString());
    // dispatch ngrx action to remove users
    this.store.dispatch(RolePermActions.removeUsersFromRoleInit({guids}));
    // deselect all nodes
    this.gridApi.deselectAll();
  }

  /**
   * This function is called every time the user searches something in the
   * search bar.
   *
   */
   /**
   * Runs the search bar's keyword against the listing's own endpoint.
   *
   * Replaces the raw SELECT that went through SubQueryService:
   *
   *   FROM app_mst_link_subject_to_role hdr
   *   LEFT JOIN app_login_subject subject ON subject.guid = hdr.app_subject_guid
   *   WHERE (subject.main_email ILIKE '%x%' OR subject.main_phone ILIKE '%x%')
   *     AND hdr.app_mst_role_guid = '<role>' AND hdr.status != 'DELETED'
   *
   * GET iam/team-role-links/user-to-role/query -> LinkSubjectToRoleUow ->
   * generateWhereClauseByCriteria joins app_login_subject and app_login_principal
   * itself and applies `search_word` as a bound ILIKE over main_email, main_phone
   * and principal_id, with `status != 'DELETED'` as its default. The
   * `app_mst_role_guid` scoping stays in this component's default criteria, which
   * getDatasource concatenates with what is passed in.
   *
   * Behaviour note: the keyword also matches the login principal id.
   */
  onSearch() {
    const datasource = this.getDatasource(this.buildSearchCriteria());
    this.gridApi.setServerSideDatasource(datasource);
  }

  /**
   * Criteria for the current keyword, or undefined when the box is empty so the
   * listing falls back to its default criteria.
   */
  buildSearchCriteria() {
    const keyword = (this.searchValue || '').toString().trim();
    if (!keyword) {
      return undefined;
    }
    return [
      { columnName: 'search_word', operator: '=', value: keyword }
    ];
  }

  /**
   * Reset the search bar and search value.
   */
  reset() {
    this.searchValue = '';
    const datasource = this.getDatasource();
    this.gridApi.setServerSideDatasource(datasource);
  };

  /**
   * Lifecycle method: Called when component is destroyed.
   */
   ngOnDestroy() {
    this.subSink.unsubscribe();
  }

}
